import { select, selectAll } from "d3";

import { Action, ActionType, FrameStencils, Marker, MarkerType, Point, StencilType } from "../../types";
import {
  getBallActions,
  getBallAndPossessionActions,
  getBallMarkers,
  getClosestStencilToPoint,
  getPlayerAndPossessionActions,
  getPlayerMarkers,
} from "../../utils";

/** ================================
 * Get Comparison Stencils
 ================================ */
export interface GetComparisonStencilsProps {
  stencil: Action | Marker;
  frameStencils: FrameStencils;
  nodePoint?: "start" | "end";
}
export interface ActionComparisonStencils {
  start: FrameStencils;
  end: FrameStencils;
  stencilType: StencilType.ACTION;
  actionRelativeEndPosition: Point;
}
export interface MarkerComparisonStencils {
  start: FrameStencils;
  end: FrameStencils;
  stencilType: StencilType.MARKER;
}
export type ComparisonStencils = ActionComparisonStencils | MarkerComparisonStencils;
export const getComparisonStencils = ({
  stencil,
  frameStencils,
  nodePoint,
}: GetComparisonStencilsProps): ComparisonStencils => {
  const frameStencilsWithoutCurrentStencil = frameStencils.filter((frameStencil) => frameStencil.id !== stencil.id);
  const playerAndPossessionActions = getPlayerAndPossessionActions(frameStencilsWithoutCurrentStencil);
  const ballAndPossessionActions = getBallAndPossessionActions(frameStencilsWithoutCurrentStencil);
  const passActions = getBallActions(frameStencilsWithoutCurrentStencil);

  switch (stencil.stencilType) {
    case StencilType.ACTION: {
      const { actionType, pos, endPos } = stencil;
      const actionRelativeEndPosition = {
        x: !nodePoint ? endPos.x - pos.x : 0,
        y: !nodePoint ? endPos.y - pos.y : 0,
      };

      const isPlayerOrPossessionAction = actionType === ActionType.PLAYER || actionType === ActionType.POSSESSION;
      const isBallOrPossessionAction = actionType === ActionType.BALL || actionType === ActionType.POSSESSION;
      const isPassAction = actionType === ActionType.BALL;

      const playerMarkers = getPlayerMarkers(frameStencilsWithoutCurrentStencil);
      const ballMarkers = getBallMarkers(frameStencilsWithoutCurrentStencil);

      const playerMovementIds = playerMarkers.flatMap((marker) => marker.movements.map((movement) => movement.id));
      const playerActionsWithoutNext = playerAndPossessionActions.filter((action) => !action.nextPlayerAction);
      const playerActionsNotInMovements = playerAndPossessionActions.filter(
        (action) => !playerMovementIds.includes(action.id)
      );

      const ballMovementIds = ballMarkers.flatMap((marker) => marker.movements.map((movement) => movement.id));
      const ballActionsWithoutNext = ballAndPossessionActions.filter((action) => !action.nextBallAction);
      const ballActionsNotInMovements = ballAndPossessionActions.filter(
        (action) => !ballMovementIds.includes(action.id)
      );

      const start =
        nodePoint !== "end"
          ? [
              ...(isPlayerOrPossessionAction ? [...playerMarkers, ...playerActionsWithoutNext] : []),
              ...(isBallOrPossessionAction ? [...ballMarkers, ...ballActionsWithoutNext] : []),
            ]
          : [];

      const end =
        nodePoint !== "start"
          ? [
              ...(isPassAction ? playerMarkers : []),
              ...(isPlayerOrPossessionAction ? playerActionsNotInMovements : []),
              ...(isBallOrPossessionAction ? ballActionsNotInMovements : []),
            ]
          : [];

      return { start, end, stencilType: StencilType.ACTION, actionRelativeEndPosition };
    }

    case StencilType.MARKER: {
      const { markerType } = stencil;
      const isPlayerMarker = markerType === MarkerType.PLAYER;
      const hasPossession = isPlayerMarker && stencil.possession;

      const start = [
        ...(isPlayerMarker ? playerAndPossessionActions : []),
        ...(hasPossession ? ballAndPossessionActions : []),
      ];
      const end = passActions;

      return { start, end, stencilType: StencilType.MARKER };
    }
  }
};

/** ================================
 * Get Closest Stencil Point
 * ================================ */
export type GetClosestStencilPointsProps = ComparisonStencils & {
  newX: number;
  newY: number;
};
export interface ClosestStencilPoints {
  closestStencilToStart: Action | Marker | null;
  closestStencilToEnd: Action | Marker | null;
}
export const getClosestStencilPoints = (props: GetClosestStencilPointsProps): ClosestStencilPoints => {
  const { stencilType, start, end, newX, newY } = props;

  switch (stencilType) {
    case StencilType.ACTION: {
      const { actionRelativeEndPosition } = props;

      const closestStencilToStart = getClosestStencilToPoint({
        stencils: start,
        point: { x: newX, y: newY },
        actionPos: "endPos",
      });

      const newEndX = newX + actionRelativeEndPosition.x;
      const newEndY = newY + actionRelativeEndPosition.y;

      const closestStencilToEnd = getClosestStencilToPoint({
        stencils: end,
        point: { x: newEndX, y: newEndY },
      });

      return { closestStencilToStart, closestStencilToEnd };
    }
    case StencilType.MARKER: {
      const closestActionStart = getClosestStencilToPoint<Action>({
        stencils: start,
        point: { x: newX, y: newY },
      });

      const closestActionEnd = getClosestStencilToPoint<Action>({
        stencils: end,
        point: { x: newX, y: newY },
        actionPos: "endPos",
      });

      return { closestStencilToStart: closestActionStart, closestStencilToEnd: closestActionEnd };
    }
  }
};

/** ================================
 * Clear Association Circle Highlights
 * ================================ */
export const clearAssociationCircleHighlights = () => {
  selectAll(".association-circle").classed("is-active", false);
  selectAll(".association-circle-start").classed("is-active", false);
  selectAll(".association-circle-end").classed("is-active", false);
};

/** ================================
 * Highlight Associated Stencils
 * ================================ */
export type HighlightAssociatedStencilsProps = ComparisonStencils & {
  newX: number;
  newY: number;
};
export const highlightAssociatedStencils = (props: HighlightAssociatedStencilsProps) => {
  const { stencilType } = props;
  const { closestStencilToStart, closestStencilToEnd } = getClosestStencilPoints(props);

  // for ecah start and end stencil..
  [closestStencilToStart, closestStencilToEnd].forEach((closestStencil, i) => {
    const isStencilStart = i === 0;
    const isStencilEnd = i === 1;

    // if there is a closest stencil..
    if (closestStencil) {
      // selectionId will be the id of the associated player if the closest stencil is a marker and the marker is a
      // ball, otherwise it will be the id of the closest stencil
      const selectionId =
        closestStencil.stencilType === StencilType.MARKER && closestStencil.markerType === MarkerType.BALL
          ? closestStencil.associatedPlayer
          : closestStencil.id;

      // get association circle class
      const associationCircleClass = (() => {
        if (closestStencil.stencilType === StencilType.MARKER) return ".association-circle";
        else if (
          (isStencilStart && stencilType === StencilType.MARKER) ||
          (isStencilEnd && stencilType === StencilType.ACTION)
        )
          return ".association-circle-start";
        else if (
          (isStencilEnd && stencilType === StencilType.MARKER) ||
          (isStencilStart && stencilType === StencilType.ACTION)
        )
          return ".association-circle-end";
        else {
          return null;
        }
      })();

      // if there is an association circle class, highlight the association circle
      if (associationCircleClass) {
        const element = select(document.getElementById(`editable-play-surface${selectionId}`));
        const circle = element.select(associationCircleClass);
        circle.classed("is-active", true);
      }
    }
  });

  if (!closestStencilToStart && !closestStencilToEnd) {
    clearAssociationCircleHighlights();
  }
};
